import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { isProductOptionsEligible } from '@tkpod/shared';
import { Card, Button, Badge, useConfirm } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { CatalogOverviewTab } from '@/components/catalog/CatalogOverviewTab';
import { CatalogPresetsTab } from '@/components/catalog/CatalogPresetsTab';
import { CatalogBlankProductsTab } from '@/components/catalog/CatalogBlankProductsTab';
import { CatalogPricingTab } from '@/components/catalog/CatalogPricingTab';
import { CatalogOutputTab } from '@/components/catalog/CatalogOutputTab';
import { CatalogVariantMatrix } from '@/components/catalog/CatalogVariantMatrix';
import { CatalogProvidersTab } from '@/components/catalog/CatalogProvidersTab';
import { ApparelSidesTab } from '@/components/catalog/apparel/ApparelSidesTab';
import { ProductOptionsTab } from '@/components/catalog/options/ProductOptionsTab';
import { DuplicateTemplateModal } from '@/components/catalog/DuplicateTemplateModal';
import type { UptDesignContext } from '@tkpod/shared';

const DESIGNER_URL = import.meta.env.VITE_DESIGNER_URL as string;

export interface TemplateDetail {
  id: string;
  name: string;
  slug: string;
  productType: string;
  status: string;
  designerMode: string;
  publishedProductCount?: number;
  designContext: string;
  categoryId: string | null;
  categoryName: string | null;
  description: string | null;
  subType: string | null;
  version: number;
  isPhysical: boolean;
  requiresShipping: boolean;
  thumbnailUrl: string | null;
  images: Array<{ url: string; thumbnailUrl: string; type: string; isDefault: boolean }> | null;
  brand: string | null;
  modelName: string | null;
  material: string | null;
  fabricWeight: string | null;
  originCountry: string | null;
  techniques: string[];
  avgFulfillmentDays: number | null;
  isDiscontinued: boolean;
  outputTransparent: boolean;
  defaultBgType: string;
  defaultBgColor: string | null;
  defaultBgImageUrl: string | null;
  constraints: Record<string, unknown>;
  exportConfig: Record<string, unknown>;
  options: Array<{
    id: string;
    optionTypeId: string;
    optionTypeName: string;
    optionTypeSlug: string;
    uiType: string;
    position: number;
    isRequired: boolean;
    values: Array<{
      id: string;
      optionValueId: string;
      name: string;
      slug: string;
      displayName: string;
      metadata: Record<string, unknown>;
      swatchUrl: string | null;
      mockupImageUrl: string | null;
      surcharge: string | null;
      sortOrder: number;
      isActive: boolean;
    }>;
  }>;
  presets: unknown[];
  blankProducts: unknown[];
  pricingProfile: Record<string, unknown> | null;
  outputProfile: Record<string, unknown> | null;
  presetCount: number;
  createdAt: string;
  updatedAt: string;
}

const TABS_BY_TYPE: Record<string, string[]> = {
  APPAREL: ['overview', 'variants', 'sides', 'blank-products', 'providers', 'pricing', 'output'],
  DTF_BY_SIZE: ['overview', 'variants', 'blank-products', 'presets', 'pricing', 'output'],
  GANGSHEET: ['overview', 'variants', 'blank-products', 'presets', 'pricing', 'output'],
  UV_STICKER: ['overview', 'presets', 'pricing', 'output'],
  DRINKWARE: ['overview', 'variants', 'blank-products', 'providers', 'pricing', 'output'],
  ACCESSORIES: ['overview', 'variants', 'blank-products', 'providers', 'pricing', 'output'],
  DEFAULT: ['overview', 'variants', 'presets', 'pricing', 'output'],
};

const TAB_LABELS: Record<string, string> = {
  overview: 'Overview',
  variants: 'Variants',
  sides: 'Print Areas & Variants',
  'blank-products': 'Blank Products',
  presets: 'Designer Presets',
  providers: 'Providers',
  pricing: 'Pricing',
  output: 'Output',
  options: 'Product Options',
};

const STATUS_BADGE: Record<string, 'success' | 'warning' | 'default'> = {
  ACTIVE: 'success',
  DRAFT: 'warning',
  ARCHIVED: 'default',
};

export function CatalogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const [activeTab, setActiveTab] = useState('overview');
  const [isDirty, setIsDirty] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const { data, isLoading, error } = useQuery<{ data: TemplateDetail }>({
    queryKey: ['catalog-template', id],
    queryFn: () => api.get<{ data: TemplateDetail }>(`/api/v1/admin/catalog/templates/${id}`),
    enabled: !!id,
  });

  const duplicateMutation = useMutation({
    mutationFn: ({
      templateId,
      designContext,
    }: {
      templateId: string;
      designContext: UptDesignContext;
    }) =>
      api.post<{
        data: TemplateDetail;
        checklist: { warnings: string[] };
      }>(`/api/v1/admin/catalog/templates/${templateId}/duplicate`, {
        designContext,
      }),
    onSuccess: (res) => {
      setDuplicateOpen(false);
      const warnings = res.checklist?.warnings ?? [];
      toast(
        warnings.length > 0
          ? `Draft copy created. ${warnings[0]}`
          : 'Draft copy created.',
        warnings.length > 0 ? 'warning' : 'success',
      );
      navigate(`/catalog/${res.data.id}`);
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to duplicate template.', 'error');
    },
  });

  const template = data?.data;
  const rawTabs = template ? (TABS_BY_TYPE[template.productType] ?? TABS_BY_TYPE.DEFAULT) : [];
  // Tab visibility rules:
  //   • designerMode === 'NONE'    → no designer-related tabs (presets, sides)
  //   • designerMode !== 'APPAREL' → hide the apparel-specific 'sides' tab
  //     so non-apparel templates do not see an empty multi-side editor.
  const tabs = !template
    ? rawTabs
    : withProductOptionsTab(
        rawTabs.filter((t) => {
          if (template.designerMode === 'NONE') {
            return t !== 'presets' && t !== 'sides';
          }
          if (t === 'sides' && template.designerMode !== 'APPAREL') return false;
          return true;
        }),
        template,
      );

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  const handleTabChange = useCallback(async (newTab: string) => {
    if (isDirty) {
      const ok = await confirm({
        title: 'Unsaved changes',
        description: 'You have unsaved changes. Do you want to continue?',
        confirmLabel: 'Discard',
        tone: 'danger',
      });
      if (!ok) return;
      toast('Unsaved changes discarded.', 'warning');
    }
    setIsDirty(false);
    setActiveTab(newTab);
  }, [isDirty, confirm]);

  if (isLoading) {
    return <CatalogDetailSkeleton />;
  }

  if (error || !template) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-secondary">
          <svg className="h-8 w-8 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-text-primary">Template not found</h2>
        <p className="text-sm text-text-secondary text-center max-w-md">
          The template you are looking for does not exist or has been removed.
        </p>
        <Button variant="outline" onClick={() => navigate('/catalog')}>
          Back to Catalog
        </Button>
      </div>
    );
  }

  const safeTab = tabs.includes(activeTab) ? activeTab : tabs[0];

  return (
    <div className="space-y-6">
      {dialog}
      <DuplicateTemplateModal
        open={duplicateOpen}
        sourceName={template.name}
        sourceDesignContext={template.designContext}
        loading={duplicateMutation.isPending}
        onClose={() => {
          if (!duplicateMutation.isPending) setDuplicateOpen(false);
        }}
        onConfirm={(designContext) =>
          duplicateMutation.mutate({ templateId: template.id, designContext })
        }
      />
      {/* Header */}
      <Card>
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-text-primary">{template.name}</h2>
              <Badge variant={STATUS_BADGE[template.status] ?? 'default'} size="sm">
                {template.status}
              </Badge>
              <Badge variant="default" size="sm">
                {template.productType.replace(/_/g, ' ')}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-text-secondary">
              {template.slug} · {template.designerMode} · {template.categoryName ?? 'No Category'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {template.designerMode !== 'NONE' && (
              <Button
                size="sm"
                onClick={async () => {
                  const presetArr = (template.presets ?? []) as Array<{ maxWidth?: number | null; maxHeight?: number | null }>;
                  const hasUnconfigured = presetArr.some(
                    (p) => p.maxWidth == null || p.maxHeight == null,
                  );
                  if (presetArr.length === 0 || hasUnconfigured) {
                    const ok = await confirm({
                      title: 'Designer presets not configured',
                      description: 'Some designer presets are missing max dimensions. The designer may not work correctly.',
                      confirmLabel: 'Continue',
                    });
                    if (!ok) return;
                  }
                  window.open(`${DESIGNER_URL}/designer?uptId=${template.id}&adminContext=true`, '_blank');
                }}
              >
                <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                </svg>
                Open in Designer
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              loading={duplicateMutation.isPending}
              onClick={() => setDuplicateOpen(true)}
            >
              Duplicate
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/catalog')}>
              Back to Catalog
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex gap-6 px-2">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabChange(tab)}
              className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                safeTab === tab
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-text-secondary hover:border-border hover:text-text-primary'
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {safeTab === 'overview' && <CatalogOverviewTab template={template} onDirtyChange={handleDirtyChange} />}
      {safeTab === 'variants' && (
        <CatalogVariantMatrix
          templateId={template.id}
          designerMode={template.designerMode}
          productType={template.productType}
          publishedProductCount={template.publishedProductCount ?? 0}
          onDirtyChange={handleDirtyChange}
        />
      )}
      {safeTab === 'sides' && <ApparelSidesTab templateId={template.id} />}
      {safeTab === 'presets' && <CatalogPresetsTab templateId={template.id} presets={template.presets} designerMode={template.designerMode} onDirtyChange={handleDirtyChange} />}
      {safeTab === 'blank-products' && (
        <CatalogBlankProductsTab
          templateId={template.id}
          blankProducts={template.blankProducts}
          onDirtyChange={handleDirtyChange}
        />
      )}
      {safeTab === 'providers' && <CatalogProvidersTab templateId={template.id} onDirtyChange={handleDirtyChange} />}
      {safeTab === 'pricing' && <CatalogPricingTab templateId={template.id} pricingProfile={template.pricingProfile} onDirtyChange={handleDirtyChange} />}
      {safeTab === 'output' && <CatalogOutputTab templateId={template.id} outputProfile={template.outputProfile} onDirtyChange={handleDirtyChange} />}
      {safeTab === 'options' && (
        <ProductOptionsTab
          templateId={template.id}
          productType={template.productType}
          designerMode={template.designerMode}
          pricingBlanks={pricingBlanksOf(template.blankProducts)}
          onOpenTab={handleTabChange}
          onDirtyChange={handleDirtyChange}
        />
      )}
    </div>
  );
}

function pricingBlanksOf(blankProducts: unknown[]): { id: string; sku: string; cost: string; portalUnitCost: string; msrp: string | null; isActive: boolean }[] {
  const blanks: { id: string; sku: string; cost: string; portalUnitCost: string; msrp: string | null; isActive: boolean }[] = [];
  for (const product of blankProducts) {
    if (!product || typeof product !== 'object') continue;
    const variants = (product as { variants?: unknown }).variants;
    if (!Array.isArray(variants)) continue;
    for (const variant of variants) {
      if (!variant || typeof variant !== 'object') continue;
      const row = variant as {
        id?: unknown;
        sku?: unknown;
        cost?: unknown;
        portalUnitCost?: unknown;
        msrp?: unknown;
        isActive?: unknown;
      };
      if (typeof row.id !== 'string' || typeof row.sku !== 'string') continue;
      const cost = typeof row.cost === 'string' ? row.cost : '0';
      blanks.push({
        id: row.id,
        sku: row.sku,
        cost,
        portalUnitCost: typeof row.portalUnitCost === 'string' ? row.portalUnitCost : cost,
        msrp: typeof row.msrp === 'string' ? row.msrp : null,
        isActive: row.isActive !== false,
      });
    }
  }
  return blanks;
}

function withProductOptionsTab(tabs: string[], template: TemplateDetail): string[] {
  if (
    !isProductOptionsEligible({
      type: template.productType,
      designerMode: template.designerMode,
    })
  ) {
    return tabs;
  }
  if (tabs.includes('options')) return tabs;
  const next = [...tabs];
  const after = next.indexOf('variants');
  next.splice(after === -1 ? 1 : after + 1, 0, 'options');
  return next;
}

function CatalogDetailSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      {/* Header card */}
      <Card>
        <div className="flex items-center justify-between px-6 py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="h-6 w-48 rounded bg-surface-tertiary" />
              <div className="h-5 w-16 rounded bg-surface-tertiary" />
              <div className="h-5 w-20 rounded bg-surface-tertiary" />
            </div>
            <div className="h-4 w-64 rounded bg-surface-tertiary" />
          </div>
          <div className="h-8 w-28 rounded bg-surface-tertiary" />
        </div>
      </Card>

      {/* Tab bar */}
      <div className="border-b border-border">
        <div className="-mb-px flex gap-6 px-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 w-20 rounded bg-surface-tertiary" />
          ))}
        </div>
      </div>

      {/* Tab content area */}
      <Card>
        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-5 w-32 rounded bg-surface-tertiary" />
            <div className="h-8 w-24 rounded bg-surface-tertiary" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 w-full rounded bg-surface-tertiary" />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
